import { Grid, Typography, Card, CardContent, Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MaskAndValid from "utils/maskAndValid";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiCard-root": {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.grey[900],
    },
    "& .MuiTypography-body1": {
      color: `${theme.palette.grey[900]} !important`,
    },
    "& .MuiTypography-body2": {
      color: `${theme.palette.text.secondary} !important`,
    },
    "& .MuiTypography-h6": {
      color: `${theme.palette.primary.main} !important`,
    },
  },
}));

const normalizeKey = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/_/g, "");

const isEmptyValue = (value) =>
  value === undefined || value === null || value === "";

const isSentinelDate = (value) => {
  if (!value) return true;
  const s = String(value);
  return s.startsWith("0001-01-01") || s.startsWith("01/01/0001");
};

/** Primeira chave definida (snake_case, camelCase ou PascalCase da API AWS). */
function pick(obj, ...keys) {
  if (!obj) return undefined;

  for (const k of keys) {
    if (!isEmptyValue(obj[k])) return obj[k];
  }

  const wanted = new Set(keys.map(normalizeKey));
  for (const [objKey, val] of Object.entries(obj)) {
    if (!isEmptyValue(val) && wanted.has(normalizeKey(objKey))) {
      return val;
    }
  }

  return undefined;
}

const formatValor = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "number") {
    return MaskAndValid.maskPrice(value);
  }
  return String(value);
};

const formatData = (value) => {
  if (!value || isSentinelDate(value)) return "-";
  const s = String(value);
  if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) return s.split(" ")[0];
  return MaskAndValid.formattDateBR(value);
};

const formatDadosAdicionais = (dados) => {
  if (!dados || typeof dados !== "object") return null;
  const entries = Object.entries(dados).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  if (entries.length === 0) return null;
  return entries.map(([k, v]) => `${k}: ${String(v)}`).join(" · ");
};

/**
 * Retorno do Report App `consulta-pedido-detail` (Ciclo / wmw).
 * API: `cabecalho_pedido` em snake_case; demais propriedades em PascalCase.
 */
export default function InvoicingRulesDisplay({ consultaPedidoDetail }) {
  const classes = useStyles();
  const d = consultaPedidoDetail || {};
  const cab = pick(d, "cabecalho_pedido", "cabecalhoPedido", "Cabecalho");
  const rex = pick(d, "regras_expedicao", "regrasExpedicao", "RegrasExpedicao");
  const rfat = pick(
    d,
    "regras_faturamento",
    "regrasFaturamento",
    "RegrasFaturamento",
  );
  const rfin = pick(
    d,
    "regras_financeiras",
    "regrasFinanceiras",
    "RegrasFinanceiras",
  );
  const itens = pick(d, "itens_pedido", "itensPedido", "ItensPedido") ?? [];
  const nfs = pick(d, "notas_fiscais", "notasFiscais", "NotasFiscais") ?? [];

  if (!consultaPedidoDetail) {
    return (
      <div className={classes.root}>
        <Card>
          <CardContent>
            <Typography variant="h4" color="primary">
              Nenhum dado do Ciclo foi carregado para este pedido.
            </Typography>
          </CardContent>
        </Card>
      </div>
    );
  }

  const numeroPedidoExibicao =
    pick(
      cab,
      "numero_pedido_completo",
      "numeroPedidoCompleto",
      "NumeroPedidoCompleto",
    ) ??
    (pick(cab, "numero_pedido", "numeroPedido", "NumeroPedido") != null &&
    pick(
      cab,
      "chave_cabecalho_pedido",
      "chaveCabecalhoPedido",
      "ChaveCabecalhoPedido",
    ) != null
      ? `${pick(cab, "numero_pedido", "numeroPedido", "NumeroPedido")}${pick(
          cab,
          "chave_cabecalho_pedido",
          "chaveCabecalhoPedido",
          "ChaveCabecalhoPedido",
        )}`
      : pick(cab, "numero_pedido", "numeroPedido", "NumeroPedido"));

  return (
    <Grid container spacing={3} className={classes.root}>
      {cab && (
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Pedido no Ciclo (cabeçalho)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Chave pedido Ciclo
                  </Typography>
                  <Typography variant="body1">
                    {pick(
                      cab,
                      "chave_pedido_ciclo",
                      "chavePedidoCiclo",
                      "ChavePedidoCiclo",
                    ) || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Número pedido
                  </Typography>
                  <Typography variant="body1">
                    {numeroPedidoExibicao?.toString() || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Código proposta (acumulado)
                  </Typography>
                  <Typography variant="body1">
                    {pick(
                      cab,
                      "codigo_pedido_acumulado",
                      "codigoPedidoAcumulado",
                      "CodigoPedidoAcumulado",
                    )?.toString() || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Código cliente
                  </Typography>
                  <Typography variant="body1">
                    {pick(
                      cab,
                      "codigo_cliente",
                      "codigoCliente",
                      "CodigoCliente",
                    )?.toString() || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Data do pedido (Ciclo)
                  </Typography>
                  <Typography variant="body1">
                    {(() => {
                      const dataPedido = pick(
                        cab,
                        "data_pedido",
                        "dataPedido",
                        "DataPedido",
                      );
                      if (!dataPedido || isSentinelDate(dataPedido)) return "-";
                      const s = String(dataPedido);
                      return /^\d{2}\/\d{2}\/\d{4}/.test(s)
                        ? s.split(" ")[0]
                        : formatData(dataPedido);
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Data prazo entrega
                  </Typography>
                  <Typography variant="body1">
                    {formatData(
                      pick(
                        cab,
                        "data_prazo_entrega",
                        "dataPrazoEntrega",
                        "DataPrazoEntrega",
                      ),
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Data previsão atendimento
                  </Typography>
                  <Typography variant="body1">
                    {formatData(
                      pick(
                        cab,
                        "data_previsao_atendimento_pedido",
                        "dataPrevisaoAtendimentoPedido",
                        "DataPrevisaoAtendimentoPedido",
                      ),
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Data fim entrega
                  </Typography>
                  <Typography variant="body1">
                    {formatData(
                      pick(
                        cab,
                        "data_fim_entrega",
                        "dataFimEntrega",
                        "DataFimEntrega",
                      ),
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Nome tipo despacho
                  </Typography>
                  <Typography variant="body1">
                    {pick(
                      cab,
                      "nome_tipo_despacho",
                      "nomeTipoDespacho",
                      "NomeTipoDespacho",
                    ) || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Transportadora redespacho
                  </Typography>
                  <Typography variant="body1">
                    {pick(
                      cab,
                      "nome_transportadora_redespacho",
                      "nomeTransportadoraRedespacho",
                      "NomeTransportadoraRedespacho",
                    ) || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Emissão NF pedido
                  </Typography>
                  <Typography variant="body1">
                    {pick(
                      cab,
                      "emissao_nota_fiscal_pedido",
                      "emissaoNotaFiscalPedido",
                      "EmissaoNotaFiscalPedido",
                    ) || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    Estoque disponível
                  </Typography>
                  <Typography variant="body1">
                    {pick(
                      cab,
                      "estoque_disponivel",
                      "estoqueDisponivel",
                      "EstoqueDisponivel",
                    ) || "-"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" marginBottom={2}>
              <LocalShippingIcon
                style={{
                  color: "#1976d2",
                  marginRight: "8px",
                  fontSize: "28px",
                }}
              />
              <Typography variant="h4" style={{ color: "#1976d2" }}>
                Regras de expedição
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h4" color="primary">
                  Motivo vencimento (expedição)
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rex,
                    "texto_motivo_vencimento_expedicao",
                    "textoMotivoVencimentoExpedicao",
                    "TextoMotivoVencimentoExpedicao",
                  ) ??
                    pick(
                      cab,
                      "texto_motivo_vencimento_expedicao",
                      "textoMotivoVencimentoExpedicao",
                      "TextoMotivoVencimentoExpedicao",
                    ) ??
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h4" color="primary">
                  Motivo cálculo
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rex,
                    "texto_motivo_calculo",
                    "textoMotivoCalculo",
                    "TextoMotivoCalculo",
                  ) ??
                    pick(
                      cab,
                      "texto_motivo_calculo",
                      "textoMotivoCalculo",
                      "TextoMotivoCalculo",
                    ) ??
                    "-"}
                </Typography>
              </Grid>
              {pick(
                rex,
                "texto_local_roteirizacao_despacho",
                "textoLocalRoteirizacaoDespacho",
                "TextoLocalRoteirizacaoDespacho",
              ) ? (
                <Grid item xs={12} md={6}>
                  <Typography variant="h4" color="primary">
                    Local roteirização despacho
                  </Typography>
                  <Typography variant="body1">
                    {pick(
                      rex,
                      "texto_local_roteirizacao_despacho",
                      "textoLocalRoteirizacaoDespacho",
                      "TextoLocalRoteirizacaoDespacho",
                    )}
                  </Typography>
                </Grid>
              ) : null}
            </Grid>
            {formatDadosAdicionais(
              pick(
                rex,
                "dados_adicionais",
                "dadosAdicionais",
                "DadosAdicionais",
              ),
            ) ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 8 }}
              >
                {formatDadosAdicionais(
                  pick(
                    rex,
                    "dados_adicionais",
                    "dadosAdicionais",
                    "DadosAdicionais",
                  ),
                )}
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" marginBottom={2}>
              <ReceiptIcon
                style={{
                  color: "#1976d2",
                  marginRight: "8px",
                  fontSize: "28px",
                }}
              />
              <Typography variant="h4" style={{ color: "#1976d2" }}>
                Regras de faturamento
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Motivo faturamento total
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rfat,
                    "texto_motivo_faturamento_total",
                    "textoMotivoFaturamentoTotal",
                    "TextoMotivoFaturamentoTotal",
                  ) ??
                    pick(
                      cab,
                      "indicador_motivo_faturamento_total",
                      "indicadorMotivoFaturamentoTotal",
                      "IndicadorMotivoFaturamentoTotal",
                    ) ??
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Motivo faturamento saldo valor
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rfat,
                    "texto_motivo_faturamento_saldo_valor",
                    "textoMotivoFaturamentoSaldoValor",
                    "TextoMotivoFaturamentoSaldoValor",
                  ) ??
                    pick(
                      cab,
                      "indicador_motivo_faturamento_saldo_valor",
                      "indicadorMotivoFaturamentoSaldoValor",
                      "IndicadorMotivoFaturamentoSaldoValor",
                    ) ??
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Emissão NF pedido
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rfat,
                    "emissao_nota_fiscal_pedido",
                    "emissaoNotaFiscalPedido",
                    "EmissaoNotaFiscalPedido",
                  ) ??
                    pick(
                      cab,
                      "emissao_nota_fiscal_pedido",
                      "emissaoNotaFiscalPedido",
                      "EmissaoNotaFiscalPedido",
                    ) ??
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={12}>
                <Typography variant="h4" color="primary">
                  Observação faturamento
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rfat,
                    "texto_observacao_faturamento",
                    "textoObservacaoFaturamento",
                    "TextoObservacaoFaturamento",
                  ) ??
                    pick(
                      cab,
                      "observacao_faturamento",
                      "observacaoFaturamento",
                      "ObservacaoFaturamento",
                    ) ??
                    "-"}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" marginBottom={2}>
              <AttachMoneyIcon
                style={{
                  color: "#1976d2",
                  marginRight: "8px",
                  fontSize: "28px",
                }}
              />
              <Typography variant="h4" style={{ color: "#1976d2" }}>
                Regras financeiras
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Código forma pagamento
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rfin,
                    "codigo_forma_pagamento",
                    "codigoFormaPagamento",
                    "CodigoFormaPagamento",
                  )?.toString() ||
                    pick(
                      cab,
                      "codigo_forma_pagamento",
                      "codigoFormaPagamento",
                      "CodigoFormaPagamento",
                    )?.toString() ||
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Código condição pagamento
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rfin,
                    "codigo_condicao_pagamento",
                    "codigoCondicaoPagamento",
                    "CodigoCondicaoPagamento",
                  )?.toString() ||
                    pick(
                      cab,
                      "codigo_condicao_pagamento",
                      "codigoCondicaoPagamento",
                      "CodigoCondicaoPagamento",
                    )?.toString() ||
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Banco portador do cliente
                </Typography>
                <Typography variant="body1">
                  {formatValor(
                    pick(
                      rfin,
                      "banco_portador_cliente",
                      "bancoPortadorCliente",
                      "BancoPortadorCliente",
                    ) ??
                      pick(
                        cab,
                        "banco_portador_cliente",
                        "bancoPortadorCliente",
                        "BancoPortadorCliente",
                      ),
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Percentual desconto duplicata
                </Typography>
                <Typography variant="body1">
                  {formatValor(
                    pick(
                      rfin,
                      "percentual_desconto_duplicata",
                      "percentualDescontoDuplicata",
                      "PercentualDescontoDuplicata",
                    ) ??
                      pick(
                        cab,
                        "percentual_desconto_duplicata",
                        "percentualDescontoDuplicata",
                        "PercentualDescontoDuplicata",
                      ),
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Vencimento no início da entrega
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rfin,
                    "vencimento_inicio_entrega",
                    "vencimentoInicioEntrega",
                    "VencimentoInicioEntrega",
                  ) ??
                    pick(
                      cab,
                      "vencimento_inicio_entrega",
                      "vencimentoInicioEntrega",
                      "VencimentoInicioEntrega",
                    ) ??
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Fora mês
                </Typography>
                <Typography variant="body1">
                  {pick(rfin, "fora_mes", "foraMes", "ForaMes") ??
                    pick(cab, "fora_mes", "foraMes", "ForaMes") ??
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Vencimento nos dias
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rfin,
                    "vencimento_nos_dias",
                    "vencimentoNosDias",
                    "VencimentoNosDias",
                  ) ??
                    pick(
                      cab,
                      "vencimento_nos_dias",
                      "vencimentoNosDias",
                      "VencimentoNosDias",
                    ) ??
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Ajuste vencimento dia útil
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rfin,
                    "ajuste_vencimento_dia_util",
                    "ajusteVencimentoDiaUtil",
                    "AjusteVencimentoDiaUtil",
                  ) ??
                    pick(
                      cab,
                      "ajuste_vencimento_dia_util",
                      "ajusteVencimentoDiaUtil",
                      "AjusteVencimentoDiaUtil",
                    ) ??
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Tipo dia cálculo vencimento
                </Typography>
                <Typography variant="body1">
                  {pick(
                    rfin,
                    "tipo_dia_calculo_vencimento",
                    "tipoDiaCalculoVencimento",
                    "TipoDiaCalculoVencimento",
                  ) ??
                    pick(
                      cab,
                      "tipo_dia_calculo_vencimento",
                      "tipoDiaCalculoVencimento",
                      "TipoDiaCalculoVencimento",
                    ) ??
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h4" color="primary">
                  Aceita valores duplicata (início / fim)
                </Typography>
                <Typography variant="body1">
                  {formatValor(
                    pick(
                      rfin,
                      "aceita_valores_duplicata_inicio",
                      "aceitaValoresDuplicataInicio",
                      "AceitaValoresDuplicataInicio",
                    ) ??
                      pick(
                        cab,
                        "aceita_valores_duplicata_inicio",
                        "aceitaValoresDuplicataInicio",
                        "AceitaValoresDuplicataInicio",
                      ),
                  )}{" "}
                  até{" "}
                  {formatValor(
                    pick(
                      rfin,
                      "aceita_valores_duplicata_fim",
                      "aceitaValoresDuplicataFim",
                      "AceitaValoresDuplicataFim",
                    ) ??
                      pick(
                        cab,
                        "aceita_valores_duplicata_fim",
                        "aceitaValoresDuplicataFim",
                        "AceitaValoresDuplicataFim",
                      ),
                  )}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {itens.length > 0 && (
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Itens do pedido (Ciclo)
              </Typography>
              {itens.map((item, idx) => (
                <Box
                  key={`${pick(item, "chave_pedido_ciclo", "chavePedidoCiclo", "ChavePedidoCiclo")}-${pick(item, "sequencia_item", "sequenciaItem", "SequenciaItem")}-${idx}`}
                  mb={1.5}
                  pb={1.5}
                  style={{
                    borderBottom:
                      idx < itens.length - 1 ? "1px solid #eee" : undefined,
                  }}
                >
                  <Typography variant="body2">
                    Seq.{" "}
                    {pick(
                      item,
                      "sequencia_item",
                      "sequenciaItem",
                      "SequenciaItem",
                    ) ?? "-"}{" "}
                    · Produto{" "}
                    {pick(
                      item,
                      "codigo_produto",
                      "codigoProduto",
                      "CodigoProduto",
                    ) ?? "-"}{" "}
                    ·{" "}
                    {pick(
                      item,
                      "nome_produto_venda",
                      "nomeProdutoVenda",
                      "NomeProdutoVenda",
                    ) || "—"}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    Qtd. planejada:{" "}
                    {formatValor(
                      pick(
                        item,
                        "quantidade_planejada",
                        "quantidadePlanejada",
                        "QuantidadePlanejada",
                      ),
                    )}{" "}
                    · Valor item: R${" "}
                    {formatValor(
                      pick(item, "valor_item", "valorItem", "ValorItem"),
                    )}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      )}

      {nfs.length > 0 && (
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Notas fiscais (Ciclo)
              </Typography>
              {nfs.map((nf, idx) => (
                <Typography
                  key={`${pick(nf, "numero_nota_fiscal", "numeroNotaFiscal", "NumeroNotaFiscal")}-${idx}`}
                  variant="body2"
                  paragraph
                >
                  NF{" "}
                  {pick(
                    nf,
                    "numero_nota_fiscal",
                    "numeroNotaFiscal",
                    "NumeroNotaFiscal",
                  ) ?? "-"}{" "}
                  · Emp.{" "}
                  {pick(
                    nf,
                    "codigo_empresa",
                    "codigoEmpresa",
                    "CodigoEmpresa",
                  ) ?? "-"}{" "}
                  · Total R${" "}
                  {formatValor(
                    pick(
                      nf,
                      "valor_total_nota_fiscal",
                      "valorTotalNotaFiscal",
                      "ValorTotalNotaFiscal",
                    ),
                  )}
                </Typography>
              ))}
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
}
